import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import errorBoundary from '@ifeng-fe/errorBoundary';
import html2canvas from 'html2canvas';
import { openAppShare } from '../Header/app';
import { getRaceView } from '../race';
import Poster from './Poster';
import styles from './index.css';

const afterPaint = () => new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

const waitImages = node =>
    Promise.all(
        Array.from(node.querySelectorAll('img')).map(img => {
            if (img.complete) return Promise.resolve();

            return new Promise(resolve => {
                img.onload = resolve;
                img.onerror = resolve;
            });
        }),
    );

const shareHint = (image, failed) => {
    if (image) return '长按图片保存至本地';
    if (failed) return '图片生成失败，可截图保存';

    return '正在生成分享图';
};

const isHarmonyClient = () => !!window.grounds && /arkweb/i.test(navigator.userAgent.toLocaleLowerCase() || '');

const shareCard = (pageConfig = {}) => ({
    shareurl: pageConfig.shareurl || '',
    title: pageConfig.title || '',
    desc: pageConfig.desc || '',
    thumbnail: pageConfig.thumb || '',
    documentId: 'ucms_8xZ5jTmnVJB',
    timestamp: String(pageConfig.updateTime || pageConfig.timestamp || ''),
});

const Share = ({ source = {}, tab = 0 }) => {
    const [open, setOpen] = useState(false);
    const [image, setImage] = useState('');
    const [failed, setFailed] = useState(false);
    const [clientShare, setClientShare] = useState(0);
    const posterRef = useRef(null);
    const sourceRef = useRef(source);
    sourceRef.current = source;
    const order = [tab, ...[0, 1, 2].filter(index => index !== tab)];
    const views = order.map(type => getRaceView(source, type));
    const showPoster = open || clientShare > 0;

    const handleShare = () => {
        if (isHarmonyClient()) {
            const pageConfig = sourceRef.current.pageConfig || {};

            openAppShare({
                url: pageConfig.shareurl,
                title: pageConfig.title,
                desc: pageConfig.desc,
                thumbnail: pageConfig.thumb,
            });

            return;
        }

        if (window.grounds) {
            setClientShare(count => count + 1);

            return;
        }

        setOpen(true);
    };

    useEffect(() => {
        if (!clientShare) return undefined;
        let cancel = false;

        (async () => {
            try {
                await afterPaint();
                const node = posterRef.current;
                if (!node || cancel) return;
                await waitImages(node);
                const canvas = await html2canvas(node, {
                    useCORS: true,
                    scale: 2,
                    backgroundColor: '#F7F7F7',
                    logging: false,
                });
                if (cancel || !window.grounds || !window.grounds.shareWebCard) return;
                window.grounds.shareWebCard(canvas.toDataURL().replace('data:image/png;base64,', ''), JSON.stringify(shareCard(sourceRef.current.pageConfig)));
            } catch (e) {
                // 客户端分享失败时不打开页面弹窗
            } finally {
                if (!cancel) setClientShare(0);
            }
        })();

        return () => {
            cancel = true;
        };
    }, [clientShare]);

    useEffect(() => {
        if (!open) return undefined;
        let cancel = false;
        setImage('');
        setFailed(false);

        (async () => {
            try {
                await afterPaint();
                const node = posterRef.current;
                if (!node || cancel) return;
                await waitImages(node);
                const canvas = await html2canvas(node, {
                    useCORS: true,
                    scale: 2,
                    backgroundColor: '#F7F7F7',
                    logging: false,
                });
                if (!cancel) setImage(canvas.toDataURL('image/png'));
            } catch (e) {
                if (!cancel) setFailed(true);
            }
        })();

        return () => {
            cancel = true;
        };
    }, [open, tab, source]);

    return (
        <>
            {open ? null : (
                <div className={styles.shareBtn} onClick={handleShare}>
                    分享实时票数
                </div>
            )}
            {showPoster ? (
                <div className={styles.offscreen}>
                    <Poster ref={posterRef} views={views} />
                </div>
            ) : null}
            {open ? (
                <div className={styles.mask}>
                    <div className={styles.sheet}>{image ? <img className={styles.shot} src={image} alt="实时票数分享图" /> : null}</div>
                    <div className={styles.actions}>
                        <div className={styles.saveHint}>{shareHint(image, failed)}</div>
                        <button type="button" className={styles.cancel} onClick={() => setOpen(false)}>
                            取消
                        </button>
                    </div>
                </div>
            ) : null}
        </>
    );
};

Share.propTypes = {
    source: PropTypes.object,
    tab: PropTypes.number,
};

export default errorBoundary(Share);
